<script lang="ts">
  import Swiper from "swiper/esm/svelte/swiper.svelte";
  import SwiperSlide from "swiper/esm/svelte/swiper-slide.svelte";
  import "swiper/swiper-bundle.min.css";
  import SwiperCore, { Navigation, Lazy } from "swiper/core";
  SwiperCore.use([Navigation, Lazy]);

  import { CardType, CarouselSize } from "$lib/gql/genCms/codegen";
  import type { Link, CardConnection } from "$lib/gql/genCms/codegen";
  import inView from "$lib/utils/inView";

  import Button from "$lib/components/Button/Button.svelte";
  import EditorialCard from "$lib/components/Cards/EditorialCard/EditorialCard.svelte";
  import ProductCard from "$lib/components/Cards/ProductCard/ProductCard.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import { onMount } from "svelte";
  import {
    ImageConfig3Card,
    ImageConfig4Card,
  } from "$lib/components/Cards/ProductCard/ProductCard.image-config";
  import findOptionStock from "$lib/utils/findOptionStock";
  import { findSizeGuideCategory } from "$lib/utils/findSizeGuideCategory";

  import client from "$lib/gql/apolloClient";

  import type { GetProductColoursQuery } from "$lib/gql/gen/codegen";
  import { GetProductColoursDoc } from "$lib/gql/gen/codegen";

  export let id: string;
  export let title = "";
  export let url: string;
  export let cta: Link;
  export let size: CarouselSize;
  export let cards: CardConnection;
  export let headingElement = "h1";
  export let controls: boolean = true;
  export let withPadding: boolean = true;
  export let twoCardsSm: boolean = false;
  export let expandSpaceBetween: boolean = false;
  export let recommendations = false;
  export let handleAttribution = undefined;
  export let isInView = false;
  export let borderBottom = true;
  export let quickBuy: boolean = false;
  export let total: number;
  export let key: string;
  export let closeSearch: () => void;
  export let nudge = false;

  let swiper;
  let fontVariant = "button1";
  let carouselSize: number;
  let slidesPerView: number;
  let imageConfig = ImageConfig3Card;

  type Options = {
    colour?: any;
    size?: any;
  };

  const onSwiper = (e: CustomEvent) => {
    const [swiperInstance] = e.detail;
    swiper = swiperInstance;
    slidesPerView = swiper.params.slidesPerView;
    swiper.on("breakpoint", (swiper, breakpointParams) => {
      slidesPerView = breakpointParams.slidesPerView;
    });
  };
  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;
  let lgWidth = 1280;
  let paddingLg = true;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    mdWidth = Number(bodyStyle.getPropertyValue("--md").replace(/\D/g, ""));
    lgWidth = Number(bodyStyle.getPropertyValue("--lg").replace(/\D/g, ""));
  });

  onMount(() => {
    if (cards.edges.length > 2) {
      if (nudge) {
        setTimeout(() => {
          swiper?.slideTo(3, 1750);
        }, 1500);

        setTimeout(() => {
          swiper?.slideTo(0, 1250);
        }, 2250);
      }
    }
  });

  $: isMobile = viewportWidth < mdWidth;
  $: isTablet = viewportWidth >= mdWidth && viewportWidth < lgWidth;
  $: isDesktop = viewportWidth >= lgWidth;

  $: if (size && size === CarouselSize.Four) {
    carouselSize = 4;
    slidesPerView = carouselSize;
    imageConfig = ImageConfig4Card;
  } else if (size === CarouselSize.Three) {
    carouselSize = 3;
    slidesPerView = carouselSize;
    imageConfig = ImageConfig3Card;
  } else {
    carouselSize = 6;
    slidesPerView = carouselSize;
    imageConfig = ImageConfig4Card;
  }

  $: if (
    (isMobile && slidesPerView > 1) ||
    (isTablet && slidesPerView >= 4) ||
    (isDesktop && slidesPerView >= 6)
  ) {
    fontVariant = "overline";
    paddingLg = false;
  }

  let selectedOptions: Options;
  let defaultColourOption;

  let productDataOverrides: GetProductColoursQuery["products"]["edges"][0][] =
    [];
  $: productDataOverrides = key && new Array(total);

  const getProduct = async (
    itemCode: string,
    selectedColourOption: string,
    currentProduct: any,
    currentIndex: number,
  ) => {
    // Get the product colours using the current item's code tag to get the product handles

    const productDetailQuery = await client.query<GetProductColoursQuery>({
      query: GetProductColoursDoc,
      variables: {
        query: itemCode,
        options: selectedOptions ? selectedOptions : [],
      },
    });

    // Filter out the selected product from the array above to get its handle and all its data
    let selectedProductOption: GetProductColoursQuery["products"]["edges"][0];
    if (itemCode && selectedColourOption) {
      selectedProductOption = productDetailQuery.data?.products?.edges?.filter(
        p => p.node?.tags?.includes(`Colour Title : ${selectedColourOption}`),
      )[0];
    }

    if (
      currentProduct.id !== selectedProductOption?.node?.id &&
      selectedProductOption
    ) {
      // store product selections in overrides array
      productDataOverrides[currentIndex] = selectedProductOption;
    }

    // set the default colour when opening the tray
    defaultColourOption = {
      identifier: selectedColourOption,
      inStock: false,
    };
  };

  const handleSelectOption = (
    index: number,
    tags: string[],
    selectedColourOption: {
      identifier: string;
      inStock?: boolean;
      index?: number;
    },
    currentProduct: any,
  ) => {
    let defaultColourOption = tags
      .filter(tag => tag.includes("Colour Title :"))
      .toString();
    let itemCode = tags.filter(tag => tag.includes("Item Code")).toString();

    getProduct(
      itemCode,
      selectedColourOption.identifier
        ? selectedColourOption.identifier
        : defaultColourOption,
      currentProduct,
      index,
    );
  };

  $: products = cards?.edges?.map((p, i) => {
    // merge any data overrides with products array

    if (
      productDataOverrides &&
      productDataOverrides.length > 0 &&
      productDataOverrides[i]
    ) {
      return {
        ...p,
        node: {
          ...p?.node,
          content: {
            ...p?.node?.content,
            product: {
              ...productDataOverrides?.[i]?.node,
              image: {
                ...productDataOverrides?.[i]?.node?.images?.edges?.[0],
                originalSrc:
                  productDataOverrides?.[i]?.node?.images?.edges?.[0]?.node
                    ?.url,
              },
              canonicalUrl:
                productDataOverrides?.[i]?.node?.canonicalUrl?.value,
              subtitle: productDataOverrides?.[i]?.node?.material?.value,
              showPrice: true,
            },
          },
        },
      };
    }

    return p;
  });
</script>

<style lang="scss">
  .carousel {
    overflow-x: hidden;

    &.borderBottom {
      border-bottom: 1px solid $background;

      @include retina {
        border-bottom: 0.5px solid $background;
      }
    }

    &--padded {
      padding: 2rem 0;
    }

    &--header {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      padding-bottom: 32px;
      align-items: flex-end;

      &--not-padded {
        padding-top: 32px;
      }

      @include breakpoint(md) {
        flex-direction: row;
        align-items: center;
      }

      &-title {
        @include breakpoint(md) {
          padding-bottom: 0;
        }
      }

      &-title-recs {
        @include breakpoint(md) {
          padding-bottom: 0;
          max-width: 100%;
        }

        @include breakpoint(lg) {
          max-width: 100%;
        }
      }
    }

    &--cards {
      opacity: 0;

      padding-bottom: 1rem;
      @include breakpoint(md) {
        padding-bottom: 2rem;
      }
      transform: translateX($animOffset);

      :global(.swiper-container) {
        margin-left: -1px;
        margin-right: -1px;

        @include retina {
          margin-left: -0.5px;
          margin-right: -0.5px;
        }
      }

      &.expandSpaceBetween {
        :global(.swiper-container) {
          margin-left: 0;
          margin-right: 0;

          @include retina {
            margin-left: 0;
            margin-right: 0;
          }
        }
      }

      @include breakpoint(lg) {
        padding-bottom: 48px;
      }
    }

    &--card {
      border-collapse: collapse;
      height: 100%;

      &__narrow-border {
        :global(.editorial-card--img-wrapper),
        :global(.product-card--img-wrapper),
        :global(.product-card--info),
        :global(.editorial-card--info) {
          margin-right: -0.5px;
          margin-left: -0.5px;
        }
      }

      :global(.editorial-card),
      :global(.product-card) {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      :global(.editorial-card--img-wrapper),
      :global(.product-card--img-wrapper) {
        border: 1px solid $background;
      }

      :global(.product-card--info),
      :global(.editorial-card--info) {
        border: 1px solid $background;
        border-top: none;
        flex: 1;
      }

      /* Fix for iPhone 12 borders being cut off */
      @include retina {
        :global(.editorial-card--img-wrapper),
        :global(.product-card--img-wrapper) {
          border: 1px solid $background;
        }

        :global(.product-card--info),
        :global(.editorial-card--info) {
          border: 1px solid $background;
          border-top: none;
        }
      }

      @include breakpoint(lg) {
        @include retina {
          :global(.editorial-card--img-wrapper),
          :global(.product-card--img-wrapper) {
            border: 0.5px solid $background;
          }

          :global(.product-card--info),
          :global(.editorial-card--info) {
            border: 0.5px solid $background;
            border-top: none;
          }

          &__narrow-border {
            :global(.editorial-card--img-wrapper),
            :global(.product-card--img-wrapper),
            :global(.product-card--info),
            :global(.editorial-card--info) {
              margin-right: -0.25px;
              margin-left: -0.25px;
            }
          }
        }
      }
    }

    &--buttons {
      display: flex;
      // padding-top: 32px;

      flex-direction: row;

      @include breakpoint(md) {
        padding-top: 0;
      }

      &--hidden {
        display: none;
      }
    }

    &-slider {
      &--prev {
        margin-right: 32px;

        @include breakpoint(md) {
          margin-right: 24px;
        }
      }
    }

    &--button {
      background-color: transparent;
      border: none;
      color: $on-background;
      padding: 0;

      &:disabled {
        color: $on-surface-30;
      }

      @include breakpoint(lg) {
        &:hover {
          color: $on-surface-30;
        }
      }
    }

    &--cta {
      display: flex;
      justify-content: center;
    }
  }

  .isInView {
    opacity: 1;
    transform: translateX(0);
    transition: all $animDuration $animFn;
  }

  :global(
      .carousel--cards > .swiper-container > .swiper-wrapper > .swiper-slide
    ) {
    height: auto;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

{#if cards && cards.edges && cards.edges.length >= 1}
  <div
    class={`carousel ${withPadding ? `carousel--padded` : ""}`}
    class:borderBottom
    data-cs-override-id="product-carousel"
    aria-label="carousel"
    role=""
  >
    {#if title || controls}
      <Container>
        <div
          class={`carousel--header ${
            !withPadding && `carousel--header--not-padded`
          }`}
        >
          {#if title}
            <div
              class={recommendations
                ? "carousel--header-title-recs"
                : "carousel--header-title"}
            >
              <Typography
                variant={isMobile ? "h2" : "h1"}
                component={headingElement}>{title}</Typography
              >
            </div>
          {/if}
          {#if controls}
            <div
              class="carousel--buttons {slidesPerView >= cards.edges.length
                ? 'carousel--buttons--hidden'
                : ''}"
            >
              <button
                class="carousel-slider--prev carousel--button"
                id="prev-{id}"
                aria-label="previous"
              >
                <Icon
                  icon="chevron_left"
                  iconStyle="rounded"
                  color="var(--on-surface)"
                  withBorder
                />
              </button>
              <button
                class="carousel-slider--next carousel--button"
                id="next-{id}"
                aria-label="next"
              >
                <Icon
                  icon="chevron_right"
                  iconStyle="rounded"
                  color="var(--on-surface)"
                  withBorder
                />
              </button>
            </div>
          {/if}
        </div>
      </Container>
    {/if}
    {#if cards}
      <div
        class="span"
        use:inView={{ threshold: 0.1 }}
        on:intersect={({ detail }) => {
          if (isInView) {
            return;
          }
          if (detail) {
            isInView = true;
          }
        }}
      >
        <div
          class="carousel--cards"
          class:isInView
          class:expandSpaceBetween
          aria-label="carousel"
          role=""
        >
          <Swiper
            on:swiper={onSwiper}
            class="carousel-slider"
            lazy={true}
            preload={false}
            navigation={{
              nextEl: `#next-${id}`,
              prevEl: `#prev-${id}`,
            }}
            breakpoints={{
              375: {
                slidesPerView: carouselSize >= 4 ? 2 : twoCardsSm ? 2 : 1,
                slidesPerGroup: carouselSize >= 4 ? 2 : twoCardsSm ? 2 : 1,
                spaceBetween: expandSpaceBetween ? 40 : 0.5,
              },
              768: {
                slidesPerView: carouselSize >= 4 ? 4 : 2,
                slidesPerGroup: carouselSize >= 4 ? 4 : 2,
                spaceBetween: expandSpaceBetween ? 40 : 0.5,
              },
              1280: {
                slidesPerView: carouselSize,
                slidesPerGroup: carouselSize,
                spaceBetween: expandSpaceBetween ? 99 : 0.5,
              },
              1600: {
                slidesPerView: carouselSize,
                slidesPerGroup: carouselSize,
                spaceBetween: expandSpaceBetween ? 128 : 0.5,
              },
            }}
            role=""
            aria-label="carousel"
          >
            {#each products as card, index}
              {#if card && card.node && card.node.content && card.node.content.product && card.node.content.product.image && card.node.content.product.image.originalSrc}
                <SwiperSlide>
                  <!-- svelte-ignore a11y-no-static-element-interactions -->
                  <div
                    class={`carousel--card ${
                      !expandSpaceBetween ? "carousel--card__narrow-border" : ""
                    }`}
                    aria-label="slide"
                    on:click={() =>
                      handleAttribution &&
                      handleAttribution(card.node.content.product.id)}
                    on:keydown
                  >
                    {#if card.node.type === CardType.Product && card.node.content.product}
                      <!-- Not possible to check against collections here so safer to use canonical url-->
                      <ProductCard
                        id={card.node.content.product.id}
                        {index}
                        title={card.node.content.product.title}
                        subtitle={card.node.content.product.subtitle}
                        priceRange={card.node.content.product.priceRange}
                        comparePriceRange={card.node.content.product
                          .compareAtPriceRange}
                        image={card.node.content.product.image}
                        url={card.node.content.product.canonicalUrl
                          ? card.node.content.product.canonicalUrl
                          : url + "/" + card.node.content.product.handle}
                        badge={card.node.content.product.badge}
                        showPrice={card.node.content.product.showPrice}
                        {fontVariant}
                        {paddingLg}
                        imgBorder
                        productOptions={card?.node?.content?.product?.options &&
                          card?.node?.content?.product?.options.length > 0 &&
                          card.node.content.product.options}
                        sizeOptions={card.node.content.product.options &&
                        card.node.content.product.options.length > 0 &&
                        card.node.content.product.options.find(
                          opt => opt.name === "size",
                        ) &&
                        card.node.content.product?.variants?.edges
                          ? findOptionStock(
                              "size",
                              card.node.content.product?.variants?.edges,
                            )
                          : card.node.content.product.options &&
                            card.node.content.product.options.find(
                              opt => opt.name === "size",
                            ) &&
                            card.node.content.product.options
                              .find(opt => opt.name === "size")
                              .values.map(value => ({ value, inStock: false }))}
                        selectedColourOption={card.node.content.product
                          .options &&
                          card.node.content.product.options.length > 0 &&
                          card.node.content.product.options
                            .find(
                              opt =>
                                opt.name === "colour" &&
                                card.node.content.product.options.find(
                                  opt => opt.name === "colour",
                                ),
                            )
                            .values.map(c => ({
                              identifier: c,
                              selectedSwatch:
                                card.node.content.product.tags &&
                                card.node.content.product.tags.length > 0 &&
                                card.node.content.product.tags
                                  .filter(t => t.includes("Colour Title"))
                                  .map(t => t.split(": ")[1])[0],
                            }))[0]}
                        productVariants={card?.node?.content?.product?.variants
                          ?.edges}
                        sizeGuide={findSizeGuideCategory(
                          card?.node?.content?.product?.sizeGuide,
                          card?.node?.content?.product?.tags,
                        )}
                        {quickBuy}
                        {imageConfig}
                        colourOptions={card?.node?.content?.product?.tags
                          ?.filter(t => t.includes("Parent Colours"))
                          .map(t => ({
                            identifier: t.split(":")[1].trim(),
                            inStock:
                              t.split(":")[2] && t.split(":")[2] === "InStock"
                                ? true
                                : t.split(":")[2] &&
                                    t.split(":")[2] === "OutOfStock"
                                  ? false
                                  : true,
                          }))}
                        }
                        on:selectColourOption={e => {
                          handleSelectOption(
                            index,
                            card?.node?.content?.product?.tags,
                            e.detail,
                            card?.node?.content?.product,
                          );
                        }}
                        {closeSearch}
                      />
                    {/if}
                  </div>
                </SwiperSlide>
              {/if}
              {#if card && card.node && card.node.content && card.node.content.image && card.node.content.image.uri}
                <SwiperSlide>
                  <div
                    class={`carousel--card ${
                      !expandSpaceBetween ? "carousel--card__narrow-border" : ""
                    }`}
                    aria-label="slide"
                  >
                    {#if card.node.type === CardType.Editorial && card.node.content}
                      <EditorialCard
                        title={card.node.content.title}
                        image={card.node.content.image}
                        ctaText={card.node.content.cta &&
                          card.node.content.cta.text}
                        url={card.node.content.cta &&
                          card.node.content.cta.href}
                        {fontVariant}
                        {paddingLg}
                        imgBorder
                        {imageConfig}
                      />
                    {/if}
                  </div>
                </SwiperSlide>
              {/if}
            {/each}
          </Swiper>
        </div>
      </div>
    {/if}

    {#if cta && cta.text && cta.href}
      <div class="carousel--cta">
        <Button label={cta.text} href={cta.href} primary />
      </div>
    {/if}
    <slot />
  </div>
{/if}
